var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from 'react';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import { WGS84, createAreaGraphic, createMarkerUrl, createGraphic, defaultSymbols, geographicPoint, isPoint } from '../constants';
const clusteringEnabled = (config) => config === true || (typeof config === 'object' && config.enabled !== false);
const clusterColor = [90, 64, 43, 0.92];
const clusterOutline = { color: [255, 255, 255, 0.95], width: 2 };
// Renderer key for a pre-aggregated grid cell, which stands for many properties.
const clusterCellKey = '__cell__';
const unclaimedClusterCellKey = '__cell_unclaimed__';
// Markers for parcels nobody has contributed to yet are keyed apart from their contributed
// counterparts so the renderer can draw the same kind two ways.
const unclaimedSuffix = '__unclaimed';
const symbolKey = (kindName, claimed) => claimed ? kindName : `${kindName !== null && kindName !== void 0 ? kindName : ''}${unclaimedSuffix}`;
const defaultSymbol = (style) => ({
    type: 'simple-marker',
    color: style.markerColor || defaultSymbols.parcel.markerColor,
    size: style.markerSize || defaultSymbols.parcel.markerSize,
    outline: { color: [255, 255, 255], width: 2 },
});
const unclaimedDefaultSymbol = (style) => ({
    type: 'simple-marker',
    color: [255, 255, 255, 0.9],
    size: style.markerSize || defaultSymbols.parcel.markerSize,
    outline: { color: style.markerColor || defaultSymbols.parcel.markerColor, width: 2.5 },
});
// Sized to match `clusterMinSize` below.
const clusterCellSymbol = () => ({
    type: 'simple-marker',
    style: 'circle',
    color: clusterColor,
    size: 28,
    outline: clusterOutline,
});
const unclaimedClusterCellSymbol = () => ({
    type: 'simple-marker',
    style: 'circle',
    color: [255, 255, 255, 0.92],
    size: 28,
    outline: { color: clusterColor, width: 2 },
});
const createRenderer = (xs, mapItemToMarker, style) => __awaiter(void 0, void 0, void 0, function* () {
    const markerSize = style.markerSize || defaultSymbols.parcel.markerSize;
    const uniqueKinds = new Map();
    xs.forEach(x => {
        const marker = mapItemToMarker(x);
        if (!!marker && !uniqueKinds.has(marker.id)) {
            uniqueKinds.set(marker.id, marker);
        }
    });
    // Every kind gets both a contributed and an unclaimed entry: which one a feature draws is
    // decided by its own `symbolKey`, and a kind can be present on the map as both.
    const uniqueValueInfos = (yield Promise.all(Array.from(uniqueKinds.values()).map((kind) => __awaiter(void 0, void 0, void 0, function* () {
        return [
            {
                value: kind.name,
                label: kind.displayName || kind.name,
                symbol: kind.color
                    ? {
                        type: 'picture-marker',
                        url: yield createMarkerUrl(kind.color, kind.icon || undefined, 48),
                        width: markerSize * 2,
                        height: markerSize * 2,
                    }
                    : defaultSymbol(style),
            },
            {
                value: `${kind.name}${unclaimedSuffix}`,
                label: `${kind.displayName || kind.name} (no stories yet)`,
                symbol: kind.color
                    ? {
                        type: 'picture-marker',
                        url: yield createMarkerUrl(kind.color, kind.icon || undefined, 48, true),
                        width: markerSize * 2,
                        height: markerSize * 2,
                    }
                    : unclaimedDefaultSymbol(style),
            },
        ];
    })))).flat();
    return {
        type: 'unique-value',
        field: 'symbolKey',
        defaultSymbol: defaultSymbol(style),
        defaultLabel: 'Property',
        uniqueValueInfos: [
            { value: clusterCellKey, label: 'Grouped properties', symbol: clusterCellSymbol() },
            { value: unclaimedClusterCellKey, label: 'Grouped properties with no stories yet', symbol: unclaimedClusterCellSymbol() },
            { value: unclaimedSuffix, label: 'No stories yet', symbol: unclaimedDefaultSymbol(style) },
            ...uniqueValueInfos,
        ],
    };
});
const propertyClusterConfig = (config) => {
    const overrides = typeof config === 'object'
        ? Object.assign({}, config) : {};
    delete overrides.enabled;
    return Object.assign({ type: 'cluster', clusterRadius: '76px', clusterMinSize: '28px', clusterMaxSize: '54px', maxScale: 5000, popupEnabled: false, 
        // Summing `count` rather than using `cluster_count` keeps the label honest when the
        // source features are pre-aggregated grid cells rather than individual properties.
        fields: [{
                name: 'count_sum',
                onStatisticField: 'count',
                statisticType: 'sum',
            }], symbol: {
            type: 'simple-marker',
            style: 'circle',
            color: clusterColor,
            outline: clusterOutline,
        }, labelingInfo: [{
                deconflictionStrategy: 'none',
                labelPlacement: 'center-center',
                labelExpressionInfo: {
                    expression: "Text($feature.count_sum, '#,###')",
                },
                symbol: {
                    type: 'text',
                    color: '#ffffff',
                    font: {
                        family: 'Noto Sans',
                        size: '12px',
                        weight: 'bold',
                    },
                },
            }] }, overrides);
};
const sqlString = (value) => `'${value.replace(/'/g, "''")}'`;
// A pre-aggregated grid cell stands for many properties and carries no kind, so it survives every
// legend toggle. Without the IS NULL half, `NULL NOT IN (...)` is NULL rather than true and hiding
// any one kind would clear every cluster off the map.
const propertyDefinitionExpression = (hiddenKinds, showUnclaimed = true) => {
    const kinds = !(hiddenKinds === null || hiddenKinds === void 0 ? void 0 : hiddenKinds.size)
        ? '1=1'
        : `(kindName IS NULL OR kindName NOT IN (${Array.from(hiddenKinds).map(sqlString).join(',')}))`;
    return showUnclaimed ? kinds : `${kinds} AND claimed = 1`;
};
export const useMapLayers = ({ mapView, mapReady, areas, items: _items, symbols, onItemClick, editMode, selectedId, configZoom, hiddenKinds, showUnclaimed = true, clustering, mapItem, mapItemToMarker, mapItemToAttributes, }) => {
    const useClusteredProperties = clusteringEnabled(clustering);
    const graphicsLayerRef = useRef(null);
    const propertiesLayerRef = useRef(null);
    const propertyFeatureLayerRef = useRef(null);
    const propertyGraphicsRef = useRef(new Map());
    const mapClickHandlerRef = useRef(null);
    const onItemClickRef = useRef(onItemClick);
    const hiddenKindsRef = useRef(hiddenKinds);
    const showUnclaimedRef = useRef(showUnclaimed);
    const mapItemRef = useRef(mapItem);
    const mapItemToMarkerRef = useRef(mapItemToMarker);
    const mapItemToAttributesRef = useRef(mapItemToAttributes);
    const declutterRef = useRef(null);
    const hasZoomedToAreasRef = useRef(false);
    mapItemRef.current = mapItem;
    mapItemToMarkerRef.current = mapItemToMarker;
    mapItemToAttributesRef.current = mapItemToAttributes;
    const [stateSelectedItem, setSelectedItem] = useState(null);
    // Keep callback ref updated
    useEffect(() => {
        onItemClickRef.current = onItemClick;
    }, [onItemClick]);
    useEffect(() => {
        hiddenKindsRef.current = hiddenKinds;
        showUnclaimedRef.current = showUnclaimed;
    }, [hiddenKinds, showUnclaimed]);
    // Initialize layers
    useEffect(() => {
        if (!mapView || !mapView.map)
            return;
        if (!graphicsLayerRef.current) {
            graphicsLayerRef.current = new GraphicsLayer();
            mapView.map.add(graphicsLayerRef.current);
        }
        if (!useClusteredProperties && !propertiesLayerRef.current) {
            propertiesLayerRef.current = new GraphicsLayer();
            mapView.map.add(propertiesLayerRef.current);
        }
        if (useClusteredProperties && propertiesLayerRef.current) {
            mapView.map.remove(propertiesLayerRef.current);
            propertiesLayerRef.current.destroy();
            propertiesLayerRef.current = null;
            propertyGraphicsRef.current.clear();
        }
        if (!useClusteredProperties && propertyFeatureLayerRef.current) {
            mapView.map.remove(propertyFeatureLayerRef.current);
            propertyFeatureLayerRef.current.destroy();
            propertyFeatureLayerRef.current = null;
        }
        // Watch map extent changes to close popup. Fires every frame of a pan.
        const extentHandle = mapView.watch('extent', () => setSelectedItem(prev => prev === null ? prev : null));
        return () => {
            extentHandle.remove();
        };
    }, [mapView, useClusteredProperties]);
    // Update areas layer
    useEffect(() => {
        if (!mapReady)
            return;
        const graphicsLayer = graphicsLayerRef.current;
        const view = mapView;
        if (!graphicsLayer || !view)
            return;
        const areaStyle = (symbols === null || symbols === void 0 ? void 0 : symbols.area) || defaultSymbols.area;
        const areasToDisplay = editMode === 'areas' && selectedId
            ? areas.filter(area => area.id !== selectedId)
            : areas;
        const areasMap = new Map(areasToDisplay.map(area => [area.id, area]));
        const graphicsToRemove = [];
        const existingIds = new Set();
        graphicsLayer.graphics.forEach(graphic => {
            const areaId = graphic.getAttribute('areaId');
            if (!areaId || !areasMap.has(areaId)) {
                graphicsToRemove.push(graphic);
            }
            else {
                existingIds.add(areaId);
            }
        });
        graphicsLayer.removeMany(graphicsToRemove);
        // Add new graphics for areas that aren't already displayed
        areasToDisplay.forEach(area => {
            if (!existingIds.has(area.id)) {
                const g = createAreaGraphic(area, areaStyle);
                if (g)
                    graphicsLayer.add(g);
            }
        });
        // Zoom to fit all area graphics only on initial load when no zoom is specified in config
        if (configZoom === undefined && graphicsLayer.graphics.length > 0 && !hasZoomedToAreasRef.current) {
            hasZoomedToAreasRef.current = true;
            view.goTo(graphicsLayer.graphics.toArray(), {
                duration: 1000,
                easing: 'ease-in-out'
            }).catch(() => {
                // Ignore animation errors
            });
        }
    }, [areas, symbols, mapReady, mapView, editMode, selectedId, configZoom]);
    // Update properties layer (diff-based: add new, remove stale, update moved)
    useEffect(() => {
        var _a;
        if (!mapReady)
            return;
        if (useClusteredProperties)
            return;
        const propertiesLayer = propertiesLayerRef.current;
        if (!propertiesLayer)
            return;
        const parcelStyle = (symbols === null || symbols === void 0 ? void 0 : symbols.parcel) || defaultSymbols.parcel;
        const existing = propertyGraphicsRef.current;
        const currentMapItem = mapItemRef.current;
        const currentMapItemToMarker = mapItemToMarkerRef.current;
        const currentMapItemToAttributes = mapItemToAttributesRef.current;
        const currentIds = new Set();
        for (const x of _items)
            currentIds.add(currentMapItem(x).id);
        // Remove stale graphics
        let staleIds = [];
        existing.forEach((graphic, id) => {
            if (!currentIds.has(id)) {
                propertiesLayer.remove(graphic);
                staleIds = staleIds.concat(id);
            }
        });
        staleIds.forEach(id => existing.delete(id));
        // Find properties that need to be added or updated
        let toAdd = [];
        for (const _item of _items) {
            const item = currentMapItem(_item);
            if (item.point.length < 2)
                continue;
            const existingGraphic = existing.get(item.id);
            const [x, y] = item.point;
            if (existingGraphic) {
                const currentKind = ((_a = currentMapItemToMarker(_item)) === null || _a === void 0 ? void 0 : _a.id) || null;
                if (existingGraphic.getAttribute('kindName') !== currentKind) {
                    propertiesLayer.remove(existingGraphic);
                    existing.delete(item.id);
                    toAdd = toAdd.concat(_item);
                }
                else if (existingGraphic.getAttribute('pointX') !== x || existingGraphic.getAttribute('pointY') !== y) {
                    existingGraphic.geometry = geographicPoint(x, y);
                    existingGraphic.setAttribute('pointX', x);
                    existingGraphic.setAttribute('pointY', y);
                }
            }
            else {
                toAdd = toAdd.concat(_item);
            }
        }
        // Add new graphics
        let cancelled = false;
        const addNewProperties = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const graphics = yield Promise.all(toAdd.map(property => createGraphic(property, currentMapItem, currentMapItemToMarker, currentMapItemToAttributes, parcelStyle)));
            if (cancelled)
                return;
            for (const g of graphics) {
                propertiesLayer.add(g);
                existing.set(g.getAttribute('propertyId'), g);
            }
            (_a = declutterRef.current) === null || _a === void 0 ? void 0 : _a.call(declutterRef);
        });
        if (toAdd.length > 0)
            addNewProperties();
        return () => { cancelled = true; };
    }, [_items, symbols, mapReady, useClusteredProperties]);
    // Update clustered properties layer
    useEffect(() => {
        if (!mapReady || !useClusteredProperties)
            return;
        const view = mapView;
        if (!(view === null || view === void 0 ? void 0 : view.map))
            return;
        const map = view.map;
        let cancelled = false;
        const parcelStyle = (symbols === null || symbols === void 0 ? void 0 : symbols.parcel) || defaultSymbols.parcel;
        const currentMapItem = mapItemRef.current;
        const currentMapItemToMarker = mapItemToMarkerRef.current;
        const currentMapItemToAttributes = mapItemToAttributesRef.current;
        const rebuildClusteredLayer = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const graphics = [];
            for (const x of _items) {
                const item = currentMapItem(x);
                if (item.point.length < 2)
                    continue;
                const marker = currentMapItemToMarker(x);
                const extra = currentMapItemToAttributes(x);
                const count = typeof extra.count === 'number' ? extra.count : 1;
                // Only the explore map distinguishes the two; every other map's items are contributed.
                const claimed = extra.claimed !== 0;
                graphics.push(new Graphic({
                    geometry: geographicPoint(item.point[0], item.point[1]),
                    attributes: Object.assign(Object.assign({ 'ObjectID': graphics.length + 1, 'itemId': item.id, 'pointX': item.point[0], 'pointY': item.point[1], 'kindName': (marker === null || marker === void 0 ? void 0 : marker.name) || null, 'kindIcon': (marker === null || marker === void 0 ? void 0 : marker.icon) || null, 'isProperty': 1 }, extra), { 'count': count, 'claimed': claimed ? 1 : 0, 'symbolKey': count > 1
                            ? (claimed ? clusterCellKey : unclaimedClusterCellKey)
                            : symbolKey((_a = marker === null || marker === void 0 ? void 0 : marker.name) !== null && _a !== void 0 ? _a : null, claimed) }),
                }));
            }
            const renderer = yield createRenderer(_items, currentMapItemToMarker, parcelStyle);
            const nextLayer = new FeatureLayer({
                title: 'Properties',
                source: graphics,
                objectIdField: 'ObjectID',
                geometryType: 'point',
                spatialReference: WGS84(),
                fields: [
                    { name: 'ObjectID', alias: 'ObjectID', type: 'oid' },
                    { name: 'itemId', alias: 'Item ID', type: 'string' },
                    { name: 'propertyId', alias: 'Property ID', type: 'string' },
                    { name: 'propertyTitle', alias: 'Property', type: 'string' },
                    { name: 'parcelId', alias: 'Parcel ID', type: 'string' },
                    { name: 'pointX', alias: 'Longitude', type: 'double' },
                    { name: 'pointY', alias: 'Latitude', type: 'double' },
                    { name: 'districtId', alias: 'District ID', type: 'string' },
                    { name: 'address', alias: 'Address', type: 'string' },
                    { name: 'kindName', alias: 'Kind', type: 'string' },
                    { name: 'symbolKey', alias: 'Symbol', type: 'string' },
                    { name: 'isProperty', alias: 'Is Property', type: 'integer' },
                    { name: 'claimed', alias: 'Claimed', type: 'integer' },
                    { name: 'count', alias: 'Properties', type: 'integer' },
                ],
                renderer,
                featureReduction: propertyClusterConfig(clustering),
                definitionExpression: propertyDefinitionExpression(hiddenKindsRef.current, showUnclaimedRef.current),
                outFields: ['*'],
                popupEnabled: false,
                legendEnabled: false,
                // A pre-aggregated cell too isolated to join a cluster still has to show its count.
                labelsVisible: true,
                labelingInfo: [{
                        labelPlacement: 'center-center',
                        where: 'count > 1',
                        labelExpressionInfo: {
                            expression: "Text($feature.count, '#,###')",
                        },
                        symbol: {
                            type: 'text',
                            color: '#ffffff',
                            haloColor: [90, 64, 43, 0.92],
                            haloSize: '6px',
                            font: {
                                family: 'Noto Sans',
                                size: '12px',
                                weight: 'bold',
                            },
                        },
                    }],
            });
            if (cancelled) {
                nextLayer.destroy();
                return;
            }
            const previous = propertyFeatureLayerRef.current;
            propertyFeatureLayerRef.current = nextLayer;
            map.add(nextLayer);
            // Hold the outgoing layer on the map until its replacement has drawn.
            if (previous) {
                try {
                    const layerView = yield view.whenLayerView(nextLayer);
                    yield reactiveUtils.whenOnce(() => !layerView.updating);
                }
                catch (_b) {
                    // the replacement was dropped before it drew; the old layer still has to go
                }
                map.remove(previous);
                previous.destroy();
            }
        });
        rebuildClusteredLayer();
        return () => {
            cancelled = true;
        };
    }, [mapView, mapReady, _items, symbols, clustering, useClusteredProperties]);
    useEffect(() => {
        if (!useClusteredProperties || !propertyFeatureLayerRef.current)
            return;
        propertyFeatureLayerRef.current.definitionExpression = propertyDefinitionExpression(hiddenKinds, showUnclaimed);
    }, [hiddenKinds, showUnclaimed, useClusteredProperties]);
    // Declutter parcel points - hide points that are too close on screen
    useEffect(() => {
        const view = mapView;
        if (!view || useClusteredProperties) {
            declutterRef.current = null;
            return;
        }
        const minPixelDistance = 15;
        const minDistSq = minPixelDistance * minPixelDistance;
        const declutterProperties = () => {
            const layer = propertiesLayerRef.current;
            if (!layer)
                return;
            if (view.zoom <= 12) {
                layer.graphics.forEach(graphic => {
                    var _a;
                    if ((_a = graphic.attributes) === null || _a === void 0 ? void 0 : _a.isProperty)
                        graphic.visible = false;
                });
                return;
            }
            const visiblePoints = [];
            layer.graphics.forEach(graphic => {
                var _a;
                if (!((_a = graphic.attributes) === null || _a === void 0 ? void 0 : _a.isProperty))
                    return;
                if ((hiddenKinds === null || hiddenKinds === void 0 ? void 0 : hiddenKinds.size) && !!graphic.attributes.kindName && hiddenKinds.has(graphic.attributes.kindName)) {
                    graphic.visible = false;
                    return;
                }
                if (!showUnclaimed && graphic.attributes.claimed === 0) {
                    graphic.visible = false;
                    return;
                }
                const geometry = graphic.geometry;
                if (!isPoint(geometry)) {
                    graphic.visible = false;
                    return;
                }
                const screenPoint = view.toScreen(geometry);
                if (!screenPoint) {
                    graphic.visible = false;
                    return;
                }
                const tooClose = visiblePoints.some(vp => {
                    const dx = screenPoint.x - vp.x;
                    const dy = screenPoint.y - vp.y;
                    return dx * dx + dy * dy < minDistSq;
                });
                graphic.visible = !tooClose;
                if (!tooClose)
                    visiblePoints.push({ x: screenPoint.x, y: screenPoint.y });
            });
        };
        declutterRef.current = declutterProperties;
        declutterProperties();
        const handle = view.watch('zoom', () => declutterProperties());
        return () => handle.remove();
    }, [mapView, mapReady, hiddenKinds, showUnclaimed, useClusteredProperties]);
    // Handle map click events for properties
    useEffect(() => {
        const view = mapView;
        if (!view)
            return;
        if (mapClickHandlerRef.current) {
            mapClickHandlerRef.current.remove();
            mapClickHandlerRef.current = null;
        }
        mapClickHandlerRef.current = view.on('click', (event) => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            const response = yield view.hitTest(event);
            const propertyHit = response.results.find((result) => { var _a; return result.type === 'graphic' && (result.graphic.isAggregate || ((_a = result.graphic.attributes) === null || _a === void 0 ? void 0 : _a.isProperty)); });
            if (!propertyHit || propertyHit.type !== 'graphic') {
                setSelectedItem(null);
                return;
            }
            if (propertyHit.graphic.isAggregate || ((_a = propertyHit.graphic.attributes) === null || _a === void 0 ? void 0 : _a.count) > 1) {
                setSelectedItem(null);
                view.goTo({
                    center: event.mapPoint,
                    zoom: Math.min(view.zoom + 2, 18),
                }, { duration: 350 }).catch(() => { });
                return;
            }
            // Show popup for property
            const attrs = propertyHit.graphic.attributes;
            const property = _items.find(x => mapItemRef.current(x).id === attrs.itemId);
            if (!property) {
                setSelectedItem(null);
                return;
            }
            (_b = onItemClickRef.current) === null || _b === void 0 ? void 0 : _b.call(onItemClickRef, property);
            setSelectedItem(Object.assign(Object.assign({}, property), { screenX: event.x + 20, screenY: event.y - 20 }));
        }));
        return () => {
            if (mapClickHandlerRef.current) {
                mapClickHandlerRef.current.remove();
                mapClickHandlerRef.current = null;
            }
        };
    }, [mapView, editMode, _items]);
    return {
        graphicsLayerRef,
        propertiesLayerRef,
        'selectedItem': stateSelectedItem,
        setSelectedItem,
    };
};
